import { ADDRESS_FIELDS } from '../../js/common/constants';
import type SingleCheckoutTypes from '../singleCheckoutTypes';

export const billingAddressFromUserAddress =
  (address: SingleCheckoutTypes.UserEnteredAddress): SingleCheckoutTypes.BillingAddress => ({
    address1: address.address1,
    address2: address.address2,
    billingPhone: address.phone,
    city: address.city,
    countryCode: address.countryCode,
    postalCode: address.postalCode,
    state: address.state,
  });

export const AddressCompatibilityError = {
  COUNTRY: 'country',
  PO_BOX: 'pobox',
  SUBDIVISION: 'subdivision',
} as const;

export const calculateCompatibilityWarning = (
  address: Partial<SingleCheckoutTypes.SavedShippingAddress>,
  restrictions: SingleCheckoutTypes.ShippingRestrictions,
) => {
  if (restrictions.disallowPOBox && (address.military || address.poBox)) {
    return AddressCompatibilityError.PO_BOX;
  }

  if (!restrictions.countries?.length) {
    return null;
  }

  const country = restrictions.countries.find((c) => c.value === address.countryCode);
  if (!country?.compatible) {
    return AddressCompatibilityError.COUNTRY;
  }

  if (country.subdivisions?.length) {
    const subdivision = country.subdivisions.find((s) => s.value === address.state);
    if (!subdivision?.compatible) {
      return AddressCompatibilityError.SUBDIVISION;
    }
  }

  return null;
};

export const deconstructName = (name: string): { firstName: string, lastName: string } => {
  const parts = name.split(' ');
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(' '),
  };
};

export const formatAddressToLine = (address: Partial<SingleCheckoutTypes.Address>): string => (
  address ? [
    address.address1,
    address.address2 ? `${address.address2},` : null,
    address.city ? `${address.city},` : null,
    address.state,
    address.postalCode,
  ].filter(Boolean).join(' ') : ''
);

export const isAddressEquals = (
  a1: Partial<SingleCheckoutTypes.ShippingAddress>,
  a2: Partial<SingleCheckoutTypes.ShippingAddress>,
) => a1 && a2
  && a1.address1 === a2.address1
  && a1.address2 === a2.address2
  && a1.city === a2.city
  && a1.countryCode === a2.countryCode
  && a1.name === a2.name
  && a1.phone === a2.phone
  && a1.postalCode === a2.postalCode
  && a1.state === a2.state;

export const isAddress = (
  a1: Partial<SingleCheckoutTypes.SavedShippingAddress>,
  a2: Partial<SingleCheckoutTypes.SavedShippingAddress>,
) => a1 && a2
  && (a1.id === a2.id || (!a1.id && !a2.id && isAddressEquals(a1, a2)));

export const isAddressCompatibilityConstraintsEquals = (
  a1: Partial<SingleCheckoutTypes.SavedShippingAddress>,
  a2: Partial<SingleCheckoutTypes.SavedShippingAddress>,
) => a1 && a2
  && (a1.military || a1.poBox) === (a2.military || a2.poBox)
  && a1.countryCode === a2.countryCode
  && a1.state === a2.state;

export const isSavedAddress =
  (address: Partial<SingleCheckoutTypes.SavedShippingAddress>): address is SingleCheckoutTypes.SavedShippingAddress => (
    !!address?.id
  );

export const postalCodeValidationPattern = (pattern: string) => {
  if (!pattern) {
    return null;
  }

  let res = pattern;
  if (!res.startsWith('^')) res = `^${res}`;
  if (!res.endsWith('$')) res = `${res}$`;
  return new RegExp(res);
};

export const shippingAddressFromUserAddress =
  (address: SingleCheckoutTypes.UserEnteredAddress): SingleCheckoutTypes.ShippingAddress => ({
    address1: address.address1,
    address2: address.address2,
    city: address.city,
    countryCode: address.countryCode,
    name: `${address.firstName?.trim()} ${address.lastName?.trim()}`.trim(),
    phone: address.phone,
    postalCode: address.postalCode,
    primary: address.primary,
    state: address.state,
  });

export const truncateBillingAddressFields = (billingAddress: SingleCheckoutTypes.BillingAddress) => {
  const truncatedAddress: SingleCheckoutTypes.BillingAddress = { ...billingAddress };

  Object.entries(truncatedAddress).forEach(([name, value]) => {
    if (ADDRESS_FIELDS[name]?.billingSize) {
      truncatedAddress[name] = (value || '').slice(0, ADDRESS_FIELDS[name].billingSize);
    }
  });

  return truncatedAddress;
};
